import { EventDetails } from '../types/event.types';

/**
 * Escapes special characters in ICS text fields according to RFC 5545
 * - Backslashes must be escaped as \\
 * - Commas, semicolons, and newlines need escaping
 * - Newlines should be replaced with \n
 */
function escapeICSValue(value: string): string {
  return value
    .replace(/\\/g, '\\\\')  // Escape backslashes first
    .replace(/,/g, '\\,')    // Escape commas
    .replace(/;/g, '\\;')     // Escape semicolons
    .replace(/\n/g, '\\n')    // Escape newlines
    .replace(/\r/g, '');      // Remove carriage returns
}

/**
 * Strips HTML tags and decodes HTML entities from a string
 */
function stripHtml(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')    // Convert <br> to newlines
    .replace(/<\/p>/gi, '\n')          // Convert closing </p> to newlines
    .replace(/<[^>]*>/g, '')           // Remove all HTML tags
    .replace(/&quot;/g, '"')           // Decode &quot;
    .replace(/&amp;/g, '&')            // Decode &amp;
    .replace(/&lt;/g, '<')             // Decode &lt;
    .replace(/&gt;/g, '>')             // Decode &gt;
    .replace(/&#39;/g, "'")            // Decode &#39;
    .replace(/&nbsp;/g, ' ')           // Decode &nbsp;
    .replace(/\n{3,}/g, '\n\n')        // Collapse multiple newlines
    .trim();
}

/**
 * Formats a Unix timestamp (seconds) to ICS date-time format (YYYYMMDDTHHmmssZ)
 * @param timestamp Unix timestamp in seconds
 * @returns ICS formatted date-time string in UTC
 */
function formatICSDateTime(timestamp: number): string {
  const date = new Date(timestamp * 1000); // Convert seconds to milliseconds
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');
  
  return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
}

/**
 * Formats a Unix timestamp (seconds) to ICS date format (YYYYMMDD) for all-day events
 * @param timestamp Unix timestamp in seconds
 * @returns ICS formatted date string
 */
function formatICSDate(timestamp: number): string {
  const date = new Date(timestamp * 1000); // Convert seconds to milliseconds
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  
  return `${year}${month}${day}`;
}

/**
 * Generates a unique identifier for the event
 * @param eventID The event ID
 * @returns A unique UID string
 */
function generateUID(eventID: string): string {
  const timestamp = Date.now();
  return `${eventID}-${timestamp}@lodos.io`;
}

/**
 * Generates ICS file content from event details
 * @param event Event details object
 * @param eventUrl The URL to the event page
 * @param locationLabel Translated label for event location (e.g., "Event Location")
 * @returns ICS file content as a string
 */
export function generateICSContent(event: EventDetails, eventUrl: string, locationLabel: string = 'Event Location'): string {
  const lines: string[] = [];
  
  // Calendar header
  lines.push('BEGIN:VCALENDAR');
  lines.push('VERSION:2.0');
  lines.push('PRODID:-//Lodos//Event Calendar//EN');
  lines.push('CALSCALE:GREGORIAN');
  lines.push('METHOD:PUBLISH');
  
  // Check if event spans multiple days
  const startDate = new Date(event.eventStartDateTime * 1000);
  const endDate = new Date(event.eventEndDateTime * 1000);
  const startDay = new Date(Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), startDate.getUTCDate()));
  const endDay = new Date(Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth(), endDate.getUTCDate()));
  const daysDiff = Math.ceil((endDay.getTime() - startDay.getTime()) / (1000 * 60 * 60 * 24));
  const isMultiDay = daysDiff > 0;
  
  // Prepare common event fields
  const title = event.eventTitle || event.title || 'Event';
  
  // Description
  let descriptionText = '';
  if (event.description) {
    const plainText = stripHtml(event.description);
    descriptionText = plainText.length > 1800
      ? plainText.substring(0, 1800) + '...'
      : plainText;
  }
  
  // Add Google Maps link if coordinates are available
  if (event.locationLatitude && event.locationLongitude && event.isOnline !== "1") {
    const parseCoordinate = (coord: string | undefined): number | null => {
      if (!coord) return null;
      const parsed = parseFloat(coord);
      return isNaN(parsed) ? null : parsed;
    };
    
    const latitude = parseCoordinate(event.locationLatitude);
    const longitude = parseCoordinate(event.locationLongitude);
    
    if (latitude !== null && longitude !== null && !isNaN(latitude) && !isNaN(longitude)) {
      const googleMapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;
      if (descriptionText) {
        descriptionText += `\n\n${locationLabel}: ${googleMapsUrl}`;
      } else {
        descriptionText = `${locationLabel}: ${googleMapsUrl}`;
      }
    }
  }
  
  // Location
  const location = event.locationAddressDefinition || event.locationAddress || '';
  const locationValue = location && event.isOnline !== "1" 
    ? location 
    : (event.isOnline === "1" ? 'Online Event' : '');
  
  // Contact information
  const contactEmail = event.contactEmail || '';
  
  // Timestamp (when this ICS file was created)
  const dtstamp = formatICSDateTime(Math.floor(Date.now() / 1000));
  
  if (isMultiDay) {
    // Single all-day event spanning multiple days
    const startDayStr = formatICSDate(event.eventStartDateTime);
    // DTEND is exclusive, so we need to add 1 day to the end date
    const endDateForDTEND = new Date(endDay);
    endDateForDTEND.setUTCDate(endDateForDTEND.getUTCDate() + 1);
    const endDayStr = formatICSDate(Math.floor(endDateForDTEND.getTime() / 1000));
    
    lines.push('BEGIN:VEVENT');
    lines.push(`UID:${generateUID(event.eventID)}`);
    lines.push(`DTSTAMP:${dtstamp}`);
    // All-day event format: DTSTART;VALUE=DATE:YYYYMMDD
    lines.push(`DTSTART;VALUE=DATE:${startDayStr}`);
    // DTEND is exclusive, so it's the day after the last day
    lines.push(`DTEND;VALUE=DATE:${endDayStr}`);
    lines.push(`SUMMARY:${escapeICSValue(title)}`);
    
    if (descriptionText) {
      lines.push(`DESCRIPTION:${escapeICSValue(descriptionText)}`);
    }
    
    if (locationValue) {
      lines.push(`LOCATION:${escapeICSValue(locationValue)}`);
    }
    
    if (eventUrl) {
      lines.push(`URL:${escapeICSValue(eventUrl)}`);
    }
    
    // Organizer - hardcoded as ToGather
    lines.push('ORGANIZER;CN=Lodos:mailto:info@lodos.io');
    
    if (contactEmail) {
      lines.push(`CONTACT:${escapeICSValue(contactEmail)}`);
    }
    
    lines.push('STATUS:CONFIRMED');
    lines.push('SEQUENCE:0');
    lines.push('END:VEVENT');
  } else {
    // Single timed event
    lines.push('BEGIN:VEVENT');
    lines.push(`UID:${generateUID(event.eventID)}`);
    lines.push(`DTSTAMP:${dtstamp}`);
    lines.push(`DTSTART:${formatICSDateTime(event.eventStartDateTime)}`);
    lines.push(`DTEND:${formatICSDateTime(event.eventEndDateTime)}`);
    lines.push(`SUMMARY:${escapeICSValue(title)}`);
    
    if (descriptionText) {
      lines.push(`DESCRIPTION:${escapeICSValue(descriptionText)}`);
    }
    
    if (locationValue) {
      lines.push(`LOCATION:${escapeICSValue(locationValue)}`);
    }
    
    if (eventUrl) {
      lines.push(`URL:${escapeICSValue(eventUrl)}`);
    }
    
    // Organizer - hardcoded as ToGather
    lines.push('ORGANIZER;CN=Lodos:mailto:info@lodos.io');
    
    if (contactEmail) {
      lines.push(`CONTACT:${escapeICSValue(contactEmail)}`);
    }
    
    lines.push('STATUS:CONFIRMED');
    lines.push('SEQUENCE:0');
    lines.push('END:VEVENT');
  }
  
  // Calendar end
  lines.push('END:VCALENDAR');
  
  // Join all lines with CRLF (RFC 5545 requires CRLF line endings)
  return lines.join('\r\n');
}

/**
 * Downloads an ICS file with the given content and filename
 * @param content ICS file content
 * @param filename Filename for the downloaded file (without .ics extension)
 */
export function downloadICSFile(content: string, filename: string): void {
  // Ensure filename ends with .ics
  const finalFilename = filename.endsWith('.ics') ? filename : `${filename}.ics`;
  
  // Create blob with calendar MIME type
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  
  // Create temporary URL
  const url = URL.createObjectURL(blob);
  
  // Create temporary anchor element
  const link = document.createElement('a');
  link.href = url;
  link.download = finalFilename;
  link.style.display = 'none';
  
  // Append to body, click, and remove
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Clean up the URL
  URL.revokeObjectURL(url);
}

/**
 * Generates and downloads an ICS file for an event
 * @param event Event details object
 * @param eventUrl The URL to the event page
 * @param locationLabel Translated label for event location (e.g., "Event Location")
 */
export function downloadEventICS(event: EventDetails, eventUrl: string, locationLabel: string = 'Event Location'): void {
  const content = generateICSContent(event, eventUrl, locationLabel);
  const title = event.eventTitle || event.title || 'event';
  // Create a safe filename by removing special characters
  const safeTitle = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const filename = `${safeTitle}-${event.eventID}`;
  downloadICSFile(content, filename);
}

